import { type QueryKey, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';
import { useCallback } from 'react';

import { Workspaces } from '@air/api';
import { type WorkspaceListResponse } from '@air/api/types';
import { isAdminDotAirDotInc } from '@air/sysadmin';
import { useIsLoggedIn } from '@air/utils-auth';

import { useAccountProvisionerContext } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';

export const WORKSPACES_LIST = 'WORKSPACES_LIST';

export type WorkspacesListKey = [typeof WORKSPACES_LIST, UseWorkspacesParams];

export const getWorkspacesListKey = (params?: Parameters<typeof Workspaces.list>['0']): WorkspacesListKey => {
  return [WORKSPACES_LIST, params];
};

export const isWorkspacesListKey = (key: QueryKey): key is WorkspacesListKey => {
  return Array.isArray(key) && key[0] === WORKSPACES_LIST && isObject(key[1]);
};

export type UseWorkspacesParams = Parameters<typeof Workspaces.list>[0];

export type UseWorkspacesOptions = {
  enabled?: boolean;
};

export const useWorkspaces = (params: UseWorkspacesParams = undefined, options?: UseWorkspacesOptions) => {
  const { isLoggedIn } = useIsLoggedIn();
  const { hasError: hasAccountProvisioningError, isFetchPending } = useAccountProvisionerContext();

  const selectWorkspaces = useCallback(
    (data: WorkspaceListResponse) =>
      hasAccountProvisioningError ? data?.filter((workspace) => !workspace.organization) : data,
    [hasAccountProvisioningError],
  );

  return useQuery({
    queryKey: getWorkspacesListKey(params),
    queryFn: () => Workspaces.list(params),
    enabled: isLoggedIn && !isFetchPending && !isAdminDotAirDotInc() && options?.enabled,
    select: selectWorkspaces,
  });
};
